import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  DEV_TIER_OVERRIDE_KEY,
  isDevTierOverrideAllowed,
} from '@/hooks/useEffectiveFeatures'
import type { SubscriptionTier } from '@busswerks/shared'

/**
 * Floating pill in the bottom-right that lets the engineer pretend
 * to be any tier without a real subscription. Renders `null` on the
 * production hostname so it never ships to end users; it's allowed
 * everywhere else (Vite dev, `dev.busswerks.io`, CloudFront previews,
 * localhost) so we can exercise gating paths against the deployed
 * dev environment too.
 *
 * Writes to localStorage under `busswerks-dev-tier-override` and
 * emits a same-tab custom event so `useEffectiveFeatures` picks up
 * the change immediately. Clicking "Clear" restores the server tier.
 */

const TIER_OPTIONS: ReadonlyArray<{ value: SubscriptionTier; label: string }> =
  [
    { value: 'free', label: 'Free' },
    { value: 'pro', label: 'Pro' },
    { value: 'studio', label: 'Studio' },
  ]

function readStored(): SubscriptionTier | null {
  try {
    const raw = window.localStorage.getItem(DEV_TIER_OVERRIDE_KEY)
    if (raw === 'free' || raw === 'pro' || raw === 'studio') return raw
  } catch {
    // ignore
  }
  return null
}

export const DevTierPanel: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState<SubscriptionTier | null>(() =>
    readStored(),
  )
  const queryClient = useQueryClient()

  if (!isDevTierOverrideAllowed()) return null

  const apply = (tier: SubscriptionTier | null) => {
    try {
      if (tier) {
        window.localStorage.setItem(DEV_TIER_OVERRIDE_KEY, tier)
      } else {
        window.localStorage.removeItem(DEV_TIER_OVERRIDE_KEY)
      }
    } catch {
      // ignore quota / sandbox errors — UI still reflects the intent
    }
    setCurrent(tier)
    window.dispatchEvent(new CustomEvent('busswerks:dev-tier-changed'))
    // Force `useAuth` to refetch `/api/v1/users/me` with the new
    // override header so every consumer (dashboard, PlanUsage, billing
    // portal) re-renders with the server-confirmed tier rather than
    // the stale pre-override snapshot. `useEffectiveFeatures` applies
    // the override synchronously for instant UI feel; this invalidation
    // makes sure the server agrees.
    void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
  }

  return (
    <div className="fixed bottom-3 right-3 z-50 font-mono">
      {open ? (
        <div className="rounded-lg bg-surface-sunken border border-brd-emphasis shadow-xl p-3 w-48 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.14em] text-content-tertiary">
              Dev tier override
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="text-xs text-content-tertiary hover:text-content-primary"
            >
              ×
            </button>
          </div>
          <div className="flex flex-col gap-1">
            {TIER_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => apply(opt.value)}
                aria-pressed={current === opt.value}
                className={`text-left px-2 py-1 rounded text-xs ${
                  current === opt.value
                    ? 'bg-accent text-[color:var(--accent-ink)]'
                    : 'text-content-secondary hover:bg-surface-raised'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => apply(null)}
            disabled={current === null}
            className="text-[10px] text-content-tertiary hover:text-content-primary disabled:opacity-40 pt-1 border-t border-brd-subtle"
          >
            Clear override
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          title="Dev-only tier override"
          className={`rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] shadow ${
            current
              ? 'bg-second text-[color:var(--second-ink,white)] border-second'
              : 'bg-surface-sunken text-content-tertiary border-brd-subtle hover:text-content-secondary'
          }`}
        >
          Tier: {current ?? 'server'}
        </button>
      )}
    </div>
  )
}
